import { processGqlDataAsArray } from '../../use-builders-to-process-gql-data';
import type { HtmlFromDotcms } from '../converters-from-dotcms';
import {
  convertDotcmsBoolean,
  convertDotcmsString,
  convertDotcmsResponseToRecord,
  wrapDotcmsHtml,
  wrapDotcmsHtmlOrUndefined,
} from '../converters-from-dotcms';
import { CategoryGqlHelper } from '../dotcms-category';
import type { IImageData } from '../dotcms-image';
import { ImageGqlHelper } from '../dotcms-image';
import { buildGqlFieldsQuery } from '../gql-builders';
import type { IGqlHelper } from '../gql-helper';
import { getActiveDotcmsPlanType } from '../dotcms-plan-type-category';
import type { IDotcmsItemRestrictedBasedOnPlanType } from '../dotcms-item-restricted-based-on-plan-type';
import type { IDotcmsItemRestrictedBasedOnCountryJurisdiction } from '../dotcms-item-restricted-based-on-country-jurisdiction';
import { processAllowOrBlockList } from '../dotcms-item-restricted-based-on-country-jurisdiction';
import { DotKeyValueGqlHelper } from '../dotcms-dotkeyvalue';
import {
  ResourceType,
  getResourceTypeFromDotcms,
} from './resource-with-thumbnail/resource-type';
import type { IBaseObjectData } from '../dotcms-base-object';
import type { IFileResourceWithThumbnailData } from './resource-with-thumbnail/dotcms-file-resource-with-thumbnail';
import type { IUrlResourceWithThumbnailData } from './resource-with-thumbnail/dotcms-url-resource-with-thumbnail';
import type {
  ResourceSectionCategory,
  IResourceLibraryCategorizableResource,
} from './resource-with-thumbnail/resource-section-category';
import { getResourceSectionCategoryFromDotcms } from './resource-with-thumbnail/resource-section-category';

export type IRelatedResourcesData = IBaseObjectData & {
  articlesAsArticles: ReadonlyArray<IArticleData>;
  articlesAsExternalFiles: ReadonlyArray<IFileResourceWithThumbnailData>;
  articlesAsExternalLinks: ReadonlyArray<IUrlResourceWithThumbnailData>;
};

export type IArticleData = Partial<IRelatedResourcesData> &
IResourceLibraryCategorizableResource &
IDotcmsItemRestrictedBasedOnPlanType &
IDotcmsItemRestrictedBasedOnCountryJurisdiction & {
  urlTitle: string;
  header: HtmlFromDotcms;
  descriptionHtml: HtmlFromDotcms;
  resourceType: ResourceType;
  image?: IImageData;
  imageHtml?: HtmlFromDotcms;
  imageAlt: string;
  contentHtml: HtmlFromDotcms;
  videoUrl?: string;
  authenticationRequiredToView: boolean;
};

export const ArticleGqlHelper: IGqlHelper<IArticleData> = {
  contentTypeName: 'Article',
  gqlFieldsQuery: `
    urlTitle
    header
    description
    resourceType { ${buildGqlFieldsQuery(CategoryGqlHelper)} }
    resourcePageSectionCategory { ${buildGqlFieldsQuery(CategoryGqlHelper)}}
    image { ${buildGqlFieldsQuery(ImageGqlHelper)} }
    imageHtml
    imageAlt
    content
    videoUrl
    restrictToPlanType { ${buildGqlFieldsQuery(CategoryGqlHelper)} }
    countryAllowList { ${DotKeyValueGqlHelper.gqlFieldsQuery} }
    countryBlockList { ${DotKeyValueGqlHelper.gqlFieldsQuery} }
    authenticationRequiredToView
  `,
  processGqlData: (gqlResponse: unknown) => {
    if (!gqlResponse) {
      return undefined;
    }

    const {
      urlTitle,
      header,
      description,
      resourceType,
      resourcePageSectionCategory,
      image,
      imageHtml,
      imageAlt,
      content,
      videoUrl,
      restrictToPlanType,
      countryAllowList,
      countryBlockList,
      authenticationRequiredToView,
    } = convertDotcmsResponseToRecord(gqlResponse);

    const contentHtmlData = wrapDotcmsHtml(content);
    if (!contentHtmlData) {
      return undefined;
    }

    const imageData = ImageGqlHelper.processGqlData(image);
    const videoUrlData = convertDotcmsString(videoUrl);

    const calculatedResourceType: ResourceType =
      !imageData && videoUrlData
        ? ResourceType.Video
        : getResourceTypeFromDotcms(resourceType);

    const calculatedSectionCategory: ResourceSectionCategory =
      getResourceSectionCategoryFromDotcms(resourcePageSectionCategory);

    return {
      urlTitle: convertDotcmsString(urlTitle) ?? '/',
      header: wrapDotcmsHtml(header) ?? { __html: '' },
      descriptionHtml: wrapDotcmsHtml(description) ?? { __html: '' },
      resourceType: calculatedResourceType,
      resourcePageSectionCategory: calculatedSectionCategory,
      image: imageData,
      imageHtml: wrapDotcmsHtmlOrUndefined(imageHtml),
      imageAlt: convertDotcmsString(imageAlt) ?? '',
      contentHtml: contentHtmlData,
      videoUrl: videoUrlData,
      authenticationRequiredToView: !!convertDotcmsBoolean(
        authenticationRequiredToView,
      ),
      restrictToPlanType: getActiveDotcmsPlanType(
        processGqlDataAsArray(
          CategoryGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          restrictToPlanType,
        ),
      ),
      countryAllowList: processAllowOrBlockList(
        processGqlDataAsArray(
          DotKeyValueGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          countryAllowList,
        ),
      ),
      countryBlockList: processAllowOrBlockList(
        processGqlDataAsArray(
          DotKeyValueGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          countryBlockList,
        ),
      ),
    };
  },
};

export const ArticleWithRelatedArticlesGqlHelper: IGqlHelper<IArticleData> = {
  contentTypeName: ArticleGqlHelper.contentTypeName,
  gqlFieldsQuery: `
    ${ArticleGqlHelper.gqlFieldsQuery}
    relatedArticles { ${buildGqlFieldsQuery(ArticleGqlHelper)} }
  `,
  processGqlData: (gqlResponse: unknown) => {
    const article = ArticleGqlHelper.processGqlData(gqlResponse);
    if (article) {
      const { relatedArticles } = convertDotcmsResponseToRecord(gqlResponse);
      article.articlesAsArticles =
        processGqlDataAsArray(
          ArticleGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          relatedArticles,
        ) ?? [];
      article.articlesAsExternalFiles = [];
      article.articlesAsExternalLinks = [];
    }

    return article;
  },
};
